import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { LineAccountStatus as LineAccountStatusType } from '../types/index';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Checkbox from '@mui/material/Checkbox';
import BusinessIcon from '@mui/icons-material/Business';

type LineAccountStatus = '' | 'has' | 'not_has' | 'unknown';
type AccountInfoKnown = '' | 'yes' | 'no';

const StoreApplication: React.FC = () => {
  const [storeName, setStoreName] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [lineAccountStatus, setLineAccountStatus] = useState<LineAccountStatus>('');
  const [accountInfoKnown, setAccountInfoKnown] = useState<AccountInfoKnown>('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus();
      }
    }, 100);
  };
  // 企業情報（必須）
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [businessType, setBusinessType] = useState('');

  // Timerexカレンダーを表示するかどうか
  const showTimerexCalendar =
    lineAccountStatus === 'not_has' ||
    lineAccountStatus === 'unknown' ||
    (lineAccountStatus === 'has' && accountInfoKnown === 'no');


  // TimeRexウィジェット: DOM要素がマウントされてからスクリプトを読み込む
  const timerexInitialized = useRef(false);

  useEffect(() => {
    if (!showTimerexCalendar) {
      timerexInitialized.current = false;
      return;
    }
    if (timerexInitialized.current) return;

    // DOM上にtimerex_calendarが存在することを確認してからスクリプトを追加
    const container = document.getElementById('timerex_calendar');
    if (!container) return;

    // 既存のスクリプトを削除して再読み込み（embed.jsはロード時に自動初期化するため）
    const oldScript = document.getElementById('timerex_embed_script');
    if (oldScript) {
      oldScript.remove();
    }

    const script = document.createElement('script');
    script.id = 'timerex_embed_script';
    script.src = 'https://asset.timerex.net/js/embed.js';
    script.onload = () => {
      if (typeof (window as any).TimerexCalendar === 'function') {
        (window as any).TimerexCalendar();
      }
    };
    document.body.appendChild(script);
    timerexInitialized.current = true;
  });

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!companyName.trim()) errors.companyName = '企業名を入力してください。';
    if (!representativeName.trim()) errors.representativeName = '代表者名を入力してください。';
    if (!storeName.trim()) errors.storeName = '店舗名を入力してください。';
    if (!storeAddress.trim()) errors.storeAddress = '店舗住所を入力してください。';
    if (!contactName.trim()) errors.contactName = '担当者名を入力してください。';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = '有効なメールアドレスを入力してください。';
    if (!phone.trim()) errors.phone = '電話番号を入力してください。';
    if (!lineAccountStatus) errors.lineAccountStatus = 'LINE公式アカウント所持状況を選択してください。';
    if (lineAccountStatus === 'has' && !accountInfoKnown) errors.accountInfoKnown = 'アカウント情報の把握状況を選択してください。';
    if (!agreedToTerms) errors.agreedToTerms = '利用規約およびプライバシーポリシーへの同意が必要です。';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return false;
    }
    setFieldErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validateForm()) return;

    setLoading(true);
    try {
      await api.storeApplication.create({
        store_name: storeName,
        store_address: storeAddress,
        contact_name: contactName,
        email: email,
        phone: phone,
        line_account_status: lineAccountStatus as LineAccountStatusType,
        account_info_known: accountInfoKnown ? (accountInfoKnown as 'yes' | 'no') : undefined,
        is_company_application: true,
        company_name: companyName,
        company_address: companyAddress || undefined,
        company_phone: companyPhone || undefined,
        representative_name: representativeName,
        business_type: businessType || undefined,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError('申込の送信に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleLineAccountStatusChange = (value: LineAccountStatus) => {
    setLineAccountStatus(value);
    // 選択変更時にサブ質問をリセット
    setAccountInfoKnown('');
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.lineAccountStatus;
      delete next.accountInfoKnown;
      return next;
    });
  };

  if (submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f8f9',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 720, width: '100%', boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            {/* ロゴ */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
              <img
                src="/recrew_logo_A-3.png"
                alt="Re:crew Logo"
                style={{ height: '40px', width: 'auto' }}
              />
            </Box>

            {/* 完了メッセージ */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#4caf50', mb: 1 }} />
              <Typography variant="h5" sx={{ mb: 1, color: '#3C3C3C', fontWeight: 600 }}>
                お申込みありがとうございます
              </Typography>
              <Typography variant="body1" color="text.secondary">
                ご入力いただいた内容を確認の上、担当者よりご連絡いたします。
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* 入力内容の確認 */}
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#3C3C3C', fontWeight: 600 }}>
              お申込み内容
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">店舗名</Typography>
                <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{storeName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">店舗住所</Typography>
                <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{storeAddress}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">担当者名</Typography>
                <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{contactName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">メールアドレス</Typography>
                <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{email}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">電話番号</Typography>
                <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{phone}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">LINE公式アカウント所持状況</Typography>
                <Typography variant="body2" sx={{ color: '#3C3C3C' }}>
                  {lineAccountStatus === 'has' ? '持っている' : lineAccountStatus === 'not_has' ? '持っていない' : 'わからない'}
                </Typography>
              </Box>
              {lineAccountStatus === 'has' && (
                <Box>
                  <Typography variant="caption" color="text.secondary">アカウント情報の把握</Typography>
                  <Typography variant="body2" sx={{ color: '#3C3C3C' }}>
                    {accountInfoKnown === 'yes' ? 'はい' : 'いいえ'}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#3C3C3C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BusinessIcon sx={{ fontSize: 18 }} />
              企業情報
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">企業名</Typography>
                <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{companyName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">代表者名</Typography>
                <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{representativeName}</Typography>
              </Box>
              {companyAddress && (
                <Box>
                  <Typography variant="caption" color="text.secondary">企業所在地</Typography>
                  <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{companyAddress}</Typography>
                </Box>
              )}
              {companyPhone && (
                <Box>
                  <Typography variant="caption" color="text.secondary">企業電話番号</Typography>
                  <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{companyPhone}</Typography>
                </Box>
              )}
              {businessType && (
                <Box>
                  <Typography variant="caption" color="text.secondary">業種</Typography>
                  <Typography variant="body2" sx={{ color: '#3C3C3C' }}>{businessType}</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100vh',
        bgcolor: '#f8f8f9',
        py: 4,
        px: 2,
      }}
    >
      <Card sx={{ maxWidth: 720, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* ロゴ */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <img
                src="/recrew_logo_A-3.png"
                alt="Re:crew Logo"
                style={{ height: '40px', width: 'auto' }}
              />
            </Box>
            <Typography variant="h6" sx={{ color: '#3C3C3C', fontWeight: 600 }}>
              店舗新規申込
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              以下の項目をご入力ください
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* 企業情報 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <BusinessIcon sx={{ fontSize: 20, color: '#3E4298' }} />
              <Typography variant="subtitle1" sx={{ color: '#3C3C3C', fontWeight: 600 }}>
                企業情報
              </Typography>
            </Box>

            {/* 企業名（必須） */}
            <TextField
              label="企業名"
              name="companyName"
              value={companyName}
              onChange={(e) => { setCompanyName(e.target.value); setFieldErrors((prev) => { const next = { ...prev }; delete next.companyName; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              placeholder="例: 株式会社○○"
              error={!!fieldErrors.companyName}
              helperText={fieldErrors.companyName}
              InputProps={{ sx: { color: '#212121' } }}
            />

            {/* 代表者名（必須） */}
            <TextField
              label="代表者名"
              name="representativeName"
              value={representativeName}
              onChange={(e) => { setRepresentativeName(e.target.value); setFieldErrors((prev) => { const next = { ...prev }; delete next.representativeName; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              placeholder="例: 田中 一郎"
              error={!!fieldErrors.representativeName}
              helperText={fieldErrors.representativeName}
              InputProps={{ sx: { color: '#212121' } }}
            />

            {/* 企業所在地（任意） */}
            <TextField
              label="企業所在地"
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              disabled={loading}
              placeholder="例: 東京都千代田区..."
              InputProps={{ sx: { color: '#212121' } }}
            />

            {/* 企業電話番号（任意） */}
            <TextField
              label="企業電話番号"
              type="tel"
              value={companyPhone}
              onChange={(e) => setCompanyPhone(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              disabled={loading}
              placeholder="例: 03-1234-5678"
              InputProps={{ sx: { color: '#212121' } }}
            />

            {/* 業種（任意） */}
            <TextField
              label="業種"
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              disabled={loading}
              placeholder="例: 飲食業、小売業、サービス業 など"
              InputProps={{ sx: { color: '#212121' } }}
            />

            <Divider sx={{ my: 3 }} />

            {/* 店舗情報 */}
            <Typography variant="subtitle1" sx={{ color: '#3C3C3C', fontWeight: 600, mb: 1 }}>
              店舗情報
            </Typography>

            {/* 店舗名 */}
            <TextField
              label="店舗名"
              name="storeName"
              value={storeName}
              onChange={(e) => { setStoreName(e.target.value); setFieldErrors((prev) => { const next = { ...prev }; delete next.storeName; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              placeholder="例: ○○店"
              error={!!fieldErrors.storeName}
              helperText={fieldErrors.storeName}
              InputProps={{ sx: { color: '#212121' } }}
            />

            {/* 店舗住所 */}
            <TextField
              label="店舗住所"
              name="storeAddress"
              value={storeAddress}
              onChange={(e) => { setStoreAddress(e.target.value); setFieldErrors((prev) => { const next = { ...prev }; delete next.storeAddress; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              placeholder="例: 東京都渋谷区..."
              error={!!fieldErrors.storeAddress}
              helperText={fieldErrors.storeAddress}
              InputProps={{ sx: { color: '#212121' } }}
            />

            <Divider sx={{ my: 3 }} />

            {/* 担当者情報 */}
            <Typography variant="subtitle1" sx={{ color: '#3C3C3C', fontWeight: 600, mb: 1 }}>
              担当者情報
            </Typography>

            {/* 担当者名 */}
            <TextField
              label="担当者名"
              name="contactName"
              value={contactName}
              onChange={(e) => { setContactName(e.target.value); setFieldErrors((prev) => { const next = { ...prev }; delete next.contactName; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              placeholder="例: 山田 太郎"
              error={!!fieldErrors.contactName}
              helperText={fieldErrors.contactName}
              InputProps={{ sx: { color: '#212121' } }}
            />

            {/* メールアドレス */}
            <TextField
              label="メールアドレス"
              name="email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setFieldErrors((prev) => { const next = { ...prev }; delete next.email; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              placeholder="例: yamada@example.com"
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              InputProps={{ sx: { color: '#212121' } }}
            />

            {/* 電話番号 */}
            <TextField
              label="電話番号"
              name="phone"
              type="tel"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setFieldErrors((prev) => { const next = { ...prev }; delete next.phone; return next; }); }}
              fullWidth
              margin="normal"
              variant="outlined"
              required
              disabled={loading}
              placeholder="例: 03-1234-5678"
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
              InputProps={{ sx: { color: '#212121' } }}
            />

            <Divider sx={{ my: 3 }} />

            {/* LINE公式アカウント所持状況 */}
            <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
              <FormLabel
                component="legend"
                sx={{
                  color: '#3C3C3C',
                  fontWeight: 500,
                  mb: 1,
                  '&.Mui-focused': { color: '#3C3C3C' },
                }}
              >
                LINE公式アカウント所持状況 *
              </FormLabel>
              <RadioGroup
                value={lineAccountStatus}
                onChange={(e) => handleLineAccountStatusChange(e.target.value as LineAccountStatus)}
              >
                <FormControlLabel
                  value="has"
                  control={<Radio sx={{ '&.Mui-checked': { color: '#1976d2' }, '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' } }} />}
                  label="持っている"
                  disabled={loading}
                  sx={{ '& .MuiFormControlLabel-label': { color: '#3C3C3C' } }}
                />
                <FormControlLabel
                  value="not_has"
                  control={<Radio sx={{ '&.Mui-checked': { color: '#1976d2' }, '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' } }} />}
                  label="持っていない"
                  disabled={loading}
                  sx={{ '& .MuiFormControlLabel-label': { color: '#3C3C3C' } }}
                />
                <FormControlLabel
                  value="unknown"
                  control={<Radio sx={{ '&.Mui-checked': { color: '#1976d2' }, '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' } }} />}
                  label="わからない"
                  disabled={loading}
                  sx={{ '& .MuiFormControlLabel-label': { color: '#3C3C3C' } }}
                />
              </RadioGroup>
              {fieldErrors.lineAccountStatus && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {fieldErrors.lineAccountStatus}
                </Typography>
              )}
            </FormControl>

            {/* 「持っている」選択時: アカウント情報の把握状況 */}
            {lineAccountStatus === 'has' && (
              <FormControl component="fieldset" sx={{ width: '100%', mb: 2, pl: 0 }}>
                <FormLabel
                  component="legend"
                  sx={{
                    color: '#3C3C3C',
                    fontWeight: 500,
                    mb: 1,
                    '&.Mui-focused': { color: '#3C3C3C' },
                  }}
                >
                  アカウント情報を把握されていますか *
                </FormLabel>
                <RadioGroup
                  value={accountInfoKnown}
                  onChange={(e) => { setAccountInfoKnown(e.target.value as AccountInfoKnown); setFieldErrors((prev) => { const next = { ...prev }; delete next.accountInfoKnown; return next; }); }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio sx={{ '&.Mui-checked': { color: '#1976d2' }, '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' } }} />}
                    label="はい"
                    disabled={loading}
                    sx={{ '& .MuiFormControlLabel-label': { color: '#3C3C3C' } }}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio sx={{ '&.Mui-checked': { color: '#1976d2' }, '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' } }} />}
                    label="いいえ"
                    disabled={loading}
                    sx={{ '& .MuiFormControlLabel-label': { color: '#3C3C3C' } }}
                  />
                </RadioGroup>
                {fieldErrors.accountInfoKnown && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {fieldErrors.accountInfoKnown}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Timerexカレンダー埋め込み表示 */}
            {showTimerexCalendar && (
              <Box
                sx={{
                  mt: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: '#F5F5F5',
                  borderRadius: '12px',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#3C3C3C', mb: 2, lineHeight: 1.8 }}
                >
                  運営よりLINE公式アカウントについてのヒアリングとご説明をさせていただきます。
                  以下のカレンダーよりご都合のよい日時をご選択ください。
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    minHeight: 400,
                    bgcolor: '#FFFFFF',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    id="timerex_calendar"
                    data-url={process.env.REACT_APP_TIMEREX_URL || ''}
                  />
                </Box>
              </Box>
            )}

            {/* 利用規約・プライバシーポリシー同意 */}
            <Box sx={{ mt: 3, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={loading}
                    sx={{ '&.Mui-checked': { color: '#3E4298' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#3C3C3C' }}>
                    <a
                      href="https://recrew-official.studio.site/terms-business"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2' }}
                    >
                      利用規約
                    </a>
                    および
                    <a
                      href="https://recrew-official.studio.site/Privacypolicy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2' }}
                    >
                      プライバシーポリシー
                    </a>
                    に同意する
                  </Typography>
                }
              />
              {fieldErrors.agreedToTerms && (
                <Typography variant="caption" color="error" sx={{ pl: 4, display: 'block' }}>
                  {fieldErrors.agreedToTerms}
                </Typography>
              )}
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 1,
                mb: 2,
                bgcolor: '#3E4298',
                '&:hover': { bgcolor: '#4b49d6' },
                '&:disabled': { bgcolor: '#9c9ce6' },
                py: 1.5,
                fontSize: '1rem',
              }}
              disabled={loading || !agreedToTerms}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                '申込む'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StoreApplication;
